<template>
    <div v-if="message">
        <ion-item :lines="hasHistory ? '' : 'none'">
            <message :message="message" v-on:refresh="$emit('refresh')" class="ion-margin-bottom" style="width: 100%;"/>
        </ion-item>
        <ion-item v-if="hasHistory && !message.is_retracted">
            <ion-list style="width: 100%;">
                <ion-list-header class="history-title">
                    {{ messageIsForwarded ? $t('ncrypt.message_history.forwarded_message_history_title') : $t('ncrypt.message_history.message_history_title') }}
                </ion-list-header>
                <ion-item v-for="(historyMessage, i) in message.history" :key="`${i}-${historyMessage.id}`">
                    <message :message="historyMessage" :is-history="true" style="width: 100%;"/>
                </ion-item>
            </ion-list>
        </ion-item>
        <div v-if="!isRetracted && writingReply">
            <write-reply class="writeReply" :original_message="message" :recipient-viewer="recipientViewer"></write-reply>
        </div>
        <div v-if="!isRetracted && writingForward">
            <write-forward class="writeForward" :original_message="message" :recipient-viewer="recipientViewer"></write-forward>
        </div>

        <ion-fab-button v-if="message && !message.is_retracted" class="page-action" @click="presentActionSheet">
            <ion-icon md="ellipsis-vertical" ios="ellipsis-vertical"></ion-icon>
        </ion-fab-button>

        <div v-if="printing">
            <print-message :message="message" :recipient-viewer="recipientViewer"></print-message>
        </div>
    </div>
</template>

<script>
    import Attachment from "../Attachment";
    import MessageParticipant from "./MessageParticipant";
    import MessageSkeleton from "./MessageSkeleton";
    import {MessageType} from "@/const/MessageType";
    import {Notifier} from "@/services/Notifier";
    import {ApiError} from "@/errors/ApiError";

    export default {
        name: "MessageWithLogic",
        components: {
            PrintMessage: () => import("../../views/Message/PrintMessage"),
            WriteForward: () => import("./WriteForward"),
            WriteReply: () => import("./WriteReply"),
            Message: () => import("./Message"),
            MessageSkeleton, MessageParticipant, Attachment,
        },
        props: {
            message: {type: Object},
            recipientViewer: {type: Object},
        },
        data() {
            return {
                historyMessage: undefined,
                writingReply: false,
                writingForward: false,
                printing: false,
            }
        },
        computed: {
            hasHistory() {
                if (!this.message) {
                    return false;
                }

                return this.message.history && this.message.history.length;
            },
            currentUserIsSender() {
                if (!this.message) {
                    return false;
                }

                if (!this.$store.getters.authed) {
                    return false;
                }

                return this.message.sender.id === this.$store.getters.user.id;
            },
            messageIsForwarded() {
                if (!this.message) {
                    return false;
                }

                return !this.isRetracted && this.message.type === MessageType.FORWARD;
            },
            isRetracted() {
                if (!this.message) {
                    return false;
                }

                return this.message.is_retracted;
            },
        },
        methods: {
            writeReply() {
                this.writingForward = false;
                this.writingReply = true;
            },
            writeForward() {
                this.writingReply = false;
                this.writingForward = true;
            },
            presentActionSheet() {
                const buttons = [
                    {
                        text: this.$t('ncrypt.close'),
                        // icon: 'close',
                        role: 'cancel',
                    },
                ];

                if (this.currentUserIsSender && !this.message.is_retracted) {
                    buttons.unshift({
                        text: this.$t('ncrypt.message_actions.retract'),
                        role: 'destructive',
                        // icon: 'trash-sharp',
                        handler: () => this.retractMessage(),
                    });
                }

                if (!this.message.is_retracted) {
                    buttons.unshift({
                        text: this.$t('ncrypt.print'),
                        // icon: 'print-sharp',
                        handler: () => {
                            this.printing = true;

                            setTimeout(() => this.printing = false, 1500);
                        },
                    });
                    buttons.unshift({
                        text: this.$t('ncrypt.forward'),
                        // icon: 'arrow-undo-outline',
                        handler: () => this.writeForward(),
                    });
                    buttons.unshift({
                        text: this.$t('ncrypt.reply_verb'),
                        // icon: 'arrow-redo',
                        handler: () => this.writeReply(),
                    });
                }

                return this.$ionic.actionSheetController
                    .create({
                        header: this.$t('ncrypt.actions'),
                        buttons,
                    })
                    .then(a => a.present())
            },
            async retractMessage() {
                if (this.message.is_retracted) {
                    await Notifier.showAlert({
                        message: this.$t('ncrypt.message_actions.already_retracted'),
                        color: 'warning',
                    });
                    return;
                }

                if (!confirm(this.$t('ncrypt.message_actions.retract_confirmation'))) {
                    return;
                }

                const loader = await this.$ionic.loadingController.create({
                    message: this.$t('ncrypt.message_actions.being_retracted'),
                    duration: 100000,
                });

                await loader.present();

                this.$http
                    .post(`/messages/${this.message.id}/retract`)
                    .then(async ({data}) => {
                        if (!data || !data.success) {
                            new ApiError(data);
                            return;
                        }

                        await Notifier.showToast({
                            message: this.$t('ncrypt.message_actions.successfully_retracted'),
                        });

                        this.$emit('refresh');
                    })
                    .catch(reason => new ApiError(reason))
                    .finally(() => loader.dismiss());
            },
        },
    }
</script>
